import {Box, HStack, Icon, Text} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

interface MobileMenuTriggerProps extends TestIdProps {
  label: string | Nullish;
  isOpen: boolean;
  isDisabled?: boolean;
}

const CHEVRON_ICON = 'navigation/arrow_down';

export function MobileMenuTrigger(props: MobileMenuTriggerProps) {
  return (
    <StyledTriggerWrapper
      aria-disabled={props.isDisabled}
      data-testid={suffixTestId('mobileMenuTrigger', props)}
      $isOpen={props.isOpen}
      $isDisabled={props.isDisabled}
    >
      <Box
        backgroundColor={getTriggerBackgroundColor(props.isOpen, props.isDisabled)}
        border="1px solid"
        borderColor={getTriggerBorderColor(props.isOpen, props.isDisabled)}
        borderRadius="small"
        height={11}
        padding={3}
        paddingRight={2}
        transition="border-color 150ms ease, box-shadow 150ms ease"
        width="100%"
      >
        <HStack justify="space-between" align="center" width="100%" height="100%">
          <Text
            alternative
            data-testid={suffixTestId('mobileMenuTriggerLabel', props)}
            size="small"
          >
            {props.label}
          </Text>

          <StyledChevronWrapper $isOpen={props.isOpen}>
            <Icon
              data-testid={suffixTestId('mobileMenuTriggerIcon', props)}
              size={4}
              value={CHEVRON_ICON}
            />
          </StyledChevronWrapper>
        </HStack>
      </Box>
    </StyledTriggerWrapper>
  );
}

function getTriggerBackgroundColor(isOpen: boolean, isDisabled?: boolean) {
  if (isDisabled) {
    return 'palettes.neutral.10.100';
  }

  if (isOpen) {
    return 'general.white';
  }

  return 'palettes.neutral.30.100';
}

function getTriggerBorderColor(isOpen: boolean, isDisabled?: boolean) {
  if (isDisabled) {
    return 'palettes.neutral.40.100';
  }

  if (isOpen) {
    return 'severity.informational';
  }

  return 'palettes.neutral.30.100';
}

type StyledTriggerWrapperProps = {
  $isOpen?: boolean;
  $isDisabled?: boolean;
};

const StyledTriggerWrapper = styled.div<StyledTriggerWrapperProps>`
  width: 100%;
  cursor: ${({$isDisabled}) => ($isDisabled ? 'not-allowed' : 'pointer')};
  outline: none;

  ${({$isDisabled, $isOpen, theme}) =>
    !$isDisabled &&
    !$isOpen &&
    css`
      &:hover > div,
      &:focus-visible > div {
        background-color: ${theme.colors.general.white};
        border-color: ${theme.colors.severity.informational};
        box-shadow: 0 0 0 ${theme.getSize(1)} ${theme.colors.palettes.blue[20][100]};
      }
    `}

  ${({$isOpen, theme}) =>
    $isOpen &&
    css`
      & > div {
        box-shadow: 0 0 0 ${theme.getSize(1)} ${theme.colors.palettes.blue[20][100]};
      }
    `}
`;

type StyledChevronWrapperProps = {
  $isOpen: boolean;
};

const StyledChevronWrapper = styled.div<StyledChevronWrapperProps>`
  display: flex;
  transform: rotate(${({$isOpen}) => ($isOpen ? '180deg' : '0deg')});
`;
